/**
 * @file index.tsx
 *
 * @fileoverview Google autocomplete for geolocation elements. By default it will lazyload so that google maps scripts
 * are only loaded when the user interacts with the input. It will place a "Dummy" input and when the user focuses that
 * Google maps scripts are loaded and the inputs are swaped maintaining focus so
 * that for the user this is not noticeable. This behaviour can be disabled with the prop "autoLoadGoogleMaps".
 */
import React, {
  createRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { AppContext } from '~/jobiqo/context/app';
import { useConfig } from '~/jobiqo/context/config';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { SERVICES } from '~/jobiqo/lib/cmp';
import { AppContextInterface } from '~/jobiqo/models/app';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import {
  createGeoFixtureSkipSuggest,
  resolveGeoFixtures,
} from './geo-fixtures';
import { GeoInput } from '~/jobiqo/types/types';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { GoogleGEOAutocomplete } from '~/vcl/atoms/01-forms/13-geocomplete/01-google';
import { GooglePlacesAutocomplete } from '~/vcl/atoms/01-forms/13-google-places';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { generateEmptyGoogleItem, getAddressComponent } from './utils';
import { MarkerIcon } from '~/vcl/icons/Marker';
import { mergeTestFixtures } from './test-fixtures';
import type { GeoFixture } from './geo-fixtures';
import HiddenVisually from '~/vcl/atoms/14-w-a11y/02-visually-hidden';

const PLACE_DETAIL_FIELDS = [
  'address_components',
  'formatted_address',
  'types',
];

// Props used by this component.
interface GooglePlacesAPIAutocompleteProps {
  /**
   * Id for the autocomplete.
   *
   * @default "geoautocomplete"
   */
  id?: string;
  /**
   * The name atribute.
   */
  name: string;
  /**
   * Callback when selecting a location.
   */
  onSuggestSelect: (item: GeoInput | null) => void;
  /**
   * Callback when value changes.
   */
  onChange?: (value: any) => void;
  /**
   * Placeholder text.
   */
  placeholder?: string;
  /**
   * Label text.
   */
  label?: string;
  /**
   * If it should allow picking from the region taxonomy.
   *
   * @default "false"
   */
  regionModal?: boolean;
  /**
   * Location string.
   */
  locationStr?: string;
  /**
   * Automatically load google maps. Will not lazy load google maps.
   *
   * @default "false"
   */
  autoLoadGoogleMaps?: boolean;
  /**
   * List of fixtures for the autocomplete.
   */
  fixtures?: string[] | GeoFixture[];
  /**
   * Flag indicating if component is used in search form.
   */
  isSearchForm?: boolean;
  /**
   * Radius parameters.
   */
  radiusParams?: {
    /**
     * Default radius value.
     */
    defaultValue?: number;
    /**
     * Array of location types we want to hide radius select box.
     */
    hideForLocationType?: string[];
    /**
     * Unit multiplier for the case of different radius unit, e.g. miles.
     */
    unitMultiplier: number;
  };
  /**
   * Types of the autocomplete.
   */
  types?: any[];
  /**
   * Restricted country predictions to the specified country
   * (ISO 3166-1 Alpha-2 country code, case insensitive). E.g., us, br, au.
   */
  country?: string[] | string;
  /**
   * The flag to clear the input onSuggestSelect,
   * when one of the fixtures gets selected.
   */
  clearFixtureOnSuggest?: boolean;
}

/*
 * Checks if the google script was loaded on the page.
 */
export const isGoogleLoaded = (): boolean => {
  // `google` can exist before `google.maps` finishes attaching.
  // @ts-ignore - Global comes from google maps script.
  return typeof google !== 'undefined' && typeof google.maps !== 'undefined';
};

/*
 * Checks if the google script was loaded on the page.
 */
export const isGoogleOnPage = (): boolean => {
  // The ConsentManager CMP changes the src of the script to data-cmp-src.
  // Therefore we need to check for both.
  // @ts-ignore - Global comes from google maps script.
  return (
    typeof window !== 'undefined' &&
    (document.querySelectorAll(
      'script[src^="https://maps.googleapis.com/maps"]',
    ).length > 0 ||
      document.querySelectorAll(
        'script[data-cmp-src^="https://maps.googleapis.com/maps"]',
      ).length > 0)
  );
};

const loadGoogleMaps = (
  setGoogleLoaded,
  setGoogleScriptAdded,
  apiKey,
  locale,
) => {
  if (typeof window !== 'undefined') {
    // Asynchronous callback for google maps.
    if (!(window as any).initMap) {
      (window as any).initMap = () => {
        setGoogleLoaded(true);
      };
    } else {
      /**
       * @description Problem: sometimes we load multiple instances of this component in a single page.
       * If an instance of google maps component was already created
       * we need to notify every instance of this component so they all know when google is done loaded.
       * Done by setting to true "setGoogleLoaded" local scope variable (use state) on each of the component's instances.
       *
       * @todo Make this better and discuss if we need to load google on top.
       * This might break encapsulation of google inside this component which initially we wanted (e.g. we can swap google with mapbox).
       * Right now the app doesn't know or care about google maps specifically it calls a generic
       * location autocomplete and all of google's loading is done here (because that generic autocomplete calls this component).
       * But because there can be multiple instances of this component this needs some coordination because otherwise we get
       * errors for google loading multiple times.
       */

      let oldInitMap = (window as any).initMap;

      // Notify the previous "version" of initMap callback but also this one.
      // Note : Do NOT convert this to an arrow function. We want a local version of "this" not a global "this"
      (window as any).initMap = function () {
        if (typeof oldInitMap === 'function') {
          oldInitMap.call(this);
        }
        setGoogleLoaded(true);
      };
    }

    // Adds google maps script asynconously
    // first it will check that the script is not already added by someone else
    // (another feature or GTM or any other) to avoid it from loading multiple times.
    if (!isGoogleOnPage()) {
      const script = document.createElement('script');
      script.id = 'google-map-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=${locale}&callback=initMap`; // tslint:disable
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      setGoogleScriptAdded(true);
    }
  }
};

/**
 * A google autocomplete for geolocations.
 */
export default function GooglePlacesAPIAutocomplete({
  id = 'geoautocomplete',
  placeholder,
  label,
  onSuggestSelect,
  onChange,
  locationStr = '',
  regionModal = false,
  autoLoadGoogleMaps = false,
  fixtures,
  isSearchForm,
  radiusParams = { unitMultiplier: 1 },
  types,
  country,
  clearFixtureOnSuggest,
  ...rest
}: GooglePlacesAPIAutocompleteProps) {
  let ref: any = createRef();

  const intl = useIntl();
  const appContext: AppContextInterface = useContext(AppContext);
  const { getConfigByName } = useConfig();
  const frontendConfig = useFrontendConfig();
  const [locationBias, setLocationBias] = useState<any>(null);
  const [googleScriptAdded, setGoogleScriptAdded] = useState(isGoogleOnPage());
  const [dummyInputFocus, setDummyInputFocus] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(isGoogleLoaded());
  const appConfig = frontendConfig.APP || {};
  const backendNewPlacesApiConfig = getConfigByName('new_places_api_enabled');
  const backendNewPlacesApiRawValue = backendNewPlacesApiConfig?.value?.[0];
  const useNewPlacesApi =
    typeof backendNewPlacesApiRawValue === 'string' &&
    (backendNewPlacesApiRawValue === '1' ||
      backendNewPlacesApiRawValue === 'true');
  const jobsConfig = frontendConfig.JOBS || {};
  const widgetOptions = appConfig.AUTOCOMPLETE_WIDGET_OPTIONS || {};
  const geoTypes = jobsConfig.GEO_INFO?.types || [];
  const autocompleteTypes = types ?? geoTypes;
  const googleAutocompleteApiKey =
    frontendConfig.API_KEYS?.GOOGLE_AUTOCOMPLETE_KEY;
  const defaultLocale = appConfig.DEFAULT_LOCALE;
  const googleGeosuggestConsent = appConfig.GOOGLE_GEOSUGGEST_CONSENT;
  const locales = appConfig.LOCALES;

  const resolvedFixtures = useMemo(
    () =>
      resolveGeoFixtures({
        fixtures,
        language: intl.locale,
        locales,
      }),
    [fixtures, intl.locale, locales],
  );

  const mappedFixures = useMemo(
    () =>
      (resolvedFixtures || []).map((fixture) => {
        return typeof fixture === 'object'
          ? fixture
          : {
              label: fixture,
            };
      }),
    [resolvedFixtures],
  );

  const finalFixtures = useMemo(() => mergeTestFixtures(mappedFixures), [
    mappedFixures,
  ]);

  const skipDuplicateSuggest = useMemo(
    () => createGeoFixtureSkipSuggest(finalFixtures),
    [finalFixtures],
  );

  useEffect(() => {
    if (!locationStr && ref.clear) {
      ref.clear();
    }
  }, [locationStr]);

  // Wait until after client-side hydration to show the component.
  useEffect(() => {
    // @ts-ignore - Global comes from google maps script.
    if (googleLoaded) {
      // If bounds are set location and radius are ignored. Default radius = 0
      // @ts-ignore - Global comes from google maps script.
      setLocationBias({
        location:
          widgetOptions.location?.lat && widgetOptions.location?.lng
            ? new (window as any).google.maps.LatLng(
                widgetOptions.location.lat,
                widgetOptions.location.lng,
              )
            : null,
        radius: widgetOptions.radius ? widgetOptions.radius : null,
        bounds:
          widgetOptions.bounds?.swlat &&
          widgetOptions.bounds?.swlng &&
          widgetOptions.bounds?.nelat &&
          widgetOptions.bounds?.nelng
            ? new (window as any).google.maps.LatLngBounds(
                new (window as any).google.maps.LatLng(
                  widgetOptions.bounds.swlat,
                  widgetOptions.bounds.swlng,
                ),
                new (window as any).google.maps.LatLng(
                  widgetOptions.bounds.nelat,
                  widgetOptions.bounds.nelng,
                ),
              )
            : null,
      });

      // When we switch inputs we want to keep focus.
      if (dummyInputFocus) {
        ref.focus();
        // Put back to false as google maps is back as main input.
        setDummyInputFocus(false);
      }
    }
  }, [googleLoaded, googleScriptAdded]);

  const userContext: UserContextInterface = useContext(UserContext);

  const isUserConsentNeededAndGiven = checkUserConsentForService(
    userContext?.consents,
    SERVICES.GoogleGeosuggest,
  );

  // Checks if google geosuggest consent is needed.
  if (googleGeosuggestConsent) {
    if (!isUserConsentNeededAndGiven) {
      // If the component is used in a search form, don't render anything.
      return isSearchForm ? null : (
        <Text marginTop={0} marginBottom={4} color="gray7">
          {intl.formatMessage({
            id: 'google_suggest_consent_request',
            defaultMessage: 'Please give consent for Google Maps Geocoding',
          })}
        </Text>
      );
    }
  }

  if (autoLoadGoogleMaps) {
    loadGoogleMaps(
      setGoogleLoaded,
      setGoogleScriptAdded,
      googleAutocompleteApiKey,
      appContext.locale || defaultLocale,
    );
  }

  return (
    <>
      {!googleLoaded || !googleScriptAdded ? (
        <Input
          size={40}
          id={id}
          label={<HiddenVisually>{label}</HiddenVisually>}
          type="text"
          Icon={
            <MarkerIcon
              color="gray3"
              title={intl.formatMessage({
                id: 'search_places',
                defaultMessage: 'Search places',
              })}
            />
          }
          iconPosition="start"
          placeholder={
            placeholder
              ? placeholder
              : intl.formatMessage({
                  id: 'search_places',
                  defaultMessage: 'Search places',
                })
          }
          onClick={() => {
            setDummyInputFocus(true);
            loadGoogleMaps(
              setGoogleLoaded,
              setGoogleScriptAdded,
              googleAutocompleteApiKey,
              appContext.locale || defaultLocale,
            );
          }}
          onChange={(e) => {
            onChange && onChange(e.currentTarget.value);
          }}
          autoComplete="off"
        />
      ) : (
        (() => {
          const GeoCompleteComponent = useNewPlacesApi
            ? GooglePlacesAutocomplete
            : GoogleGEOAutocomplete;

          return (
            <GeoCompleteComponent
              id={id}
              label={label}
              refElement={(el) => {
                ref = el;
              }}
              types={autocompleteTypes}
              {...(country || widgetOptions.country
                ? {
                    country: country ?? widgetOptions.country,
                  }
                : null)}
              Icon={
                <MarkerIcon
                  color="gray3"
                  title={intl.formatMessage({
                    id: 'search_places',
                    defaultMessage: 'Search places',
                  })}
                />
              }
              location={locationBias?.location}
              radius={locationBias?.radius}
              bounds={locationBias?.bounds}
              fixtures={finalFixtures}
              initialValue={locationStr}
              placeholder={
                placeholder
                  ? placeholder
                  : intl.formatMessage({
                      id: 'search_places',
                      defaultMessage: 'Search places',
                    })
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  // To allow resolving a location when someone just types
                  // something in the URL. The backend can try to resolve this location.
                  const emptyItem = generateEmptyGoogleItem(
                    ref.state.userInput,
                    radiusParams,
                  );
                  onSuggestSelect(emptyItem);
                }
              }}
              onSuggestSelect={(item) => {
                if (item) {
                  const commonItemProps = {
                    label: item.label,
                    lon: item.location.lng,
                    lat: item.location.lat,
                    radius:
                      (radiusParams?.defaultValue ?? 100) *
                      radiusParams?.unitMultiplier,
                  };

                  if (item.label && item.gmaps) {
                    const addressComponents =
                      item.gmaps.address_components ?? [];
                    const location: GeoInput = {
                      ...commonItemProps,
                      locationType: item.gmaps.types?.[0] ?? '',
                      location: getAddressComponent(
                        addressComponents,
                        'locality',
                      ),
                      country: getAddressComponent(
                        addressComponents,
                        'country',
                      ),
                      administrativeArea: getAddressComponent(
                        addressComponents,
                        item.gmaps.types?.[0] ?? '',
                      ),
                    };
                    onSuggestSelect(location);
                  } else {
                    if ((item as any)?.isFixture) {
                      // Fixtures are not typed in geosuggest but they come in a different format.
                      // We have to here pass a custom object because it can be that it was not geocoded.
                      const fixtureLocation: any = {
                        ...commonItemProps,
                        locationType: (item as any)?.locationType ?? 'locality',
                      };
                      // When using region fixtures we also need to specify the administrativeArea in the query.
                      if (
                        (item as any)?.locationType?.startsWith(
                          'administrative_area',
                        )
                      ) {
                        fixtureLocation.administrativeArea = item?.placeId;
                      }
                      onSuggestSelect(fixtureLocation);

                      if (clearFixtureOnSuggest && ref) {
                        ref.clear();
                      }
                    }
                  }
                } else {
                  onSuggestSelect(null);
                }
              }}
              onChange={onChange}
              autoComplete="off"
              minLength={widgetOptions.minLength}
              queryDelay={widgetOptions.queryDelay}
              skipSuggest={skipDuplicateSuggest}
              disableSuggests={Boolean(
                isSearchForm && widgetOptions.disableSuggests,
              )}
              placeDetailFields={PLACE_DETAIL_FIELDS}
              {...rest}
            />
          );
        })()
      )}
    </>
  );
}

export { GooglePlacesAPIAutocomplete };
